import React, { useState } from "react";
import ListEditor from "./ListEditor";
import { calcular, fmt, TARIFA_RAPIDA, cotizacionRapida, lineasDesdeCotizacion } from "../../utils/presupuestoCalc";
import { colors } from "../../utils/designTokens";

// Formulario de configuracion del presupuesto. Vive en el panel derecho de la pantalla.
// Recibe el config completo y un setter; cualquier cambio se propaga al preview en vivo.
//
// Props:
//   config:    PresupuestoConfig
//   onChange:  (nextConfig | (prev) => nextConfig) => void

export default function PresupuestoForm({ config, onChange, onGenerarPdf, generating = false }) {
  const c = config;
  const totals = calcular(c);

  // Cantidades de la cotizacion rapida (estado local: recien pisan el config al aplicar).
  const [quick, setQuick] = useState({ apartamentos: "", locales: "", porteros: "", limpiadoras: "", amenities: "", ascensores: "", garages: "", antiguedadAnios: "" });
  const quickQ = cotizacionRapida(quick);

  // Helper: actualiza una sola clave del config.
  const setField = (key, value) => onChange({ ...c, [key]: value });
  // Helper: actualiza un array completo (unidades / amenities / empleados).
  const setArray = (key) => (next) => onChange({ ...c, [key]: next });
  const setQuickField = (key) => (v) => setQuick((prev) => ({ ...prev, [key]: v }));
  // Reemplaza las lineas de unidades/amenities/empleados con la tarifa escalonada.
  const aplicarTarifa = () => onChange({ ...c, ...lineasDesdeCotizacion(quick) });

  return (
    <aside style={S.panel} aria-label="Configurar presupuesto">
      <header style={S.header}>
        <h2 style={S.headerTitle}>Configurar presupuesto</h2>
        <p style={S.headerSub}>Los cambios se reflejan al instante en la vista previa.</p>
      </header>

      {/* ---------- EDIFICIO ---------- */}
      <Section title="Edificio">
        <Text label="Nombre" value={c.edificio} onChange={(v) => setField("edificio", v)} />
        <Text label="Ubicación" value={c.ubicacion} onChange={(v) => setField("ubicacion", v)} />
        <Text label="Propuesta Nº" value={c.propuestaNro} onChange={(v) => setField("propuestaNro", v)} />
        <Text label="Fecha" value={c.fecha} onChange={(v) => setField("fecha", v)} />
        <Text label="Validez" value={c.validez} onChange={(v) => setField("validez", v)} />
      </Section>

      {/* ---------- CONTACTO ---------- */}
      <Section title="Contacto Habitia">
        <Text label="Email" type="email" value={c.contacto} onChange={(v) => setField("contacto", v)} />
        <Text label="Teléfono / WhatsApp" value={c.telefono} onChange={(v) => setField("telefono", v)} />
        <Text label="Web" value={c.web} onChange={(v) => setField("web", v)} />
      </Section>

      {/* ---------- COTIZACION RAPIDA ---------- */}
      <Section title="Cotización rápida · tarifa escalonada">
        <p style={S.quickHint}>
          Apartamentos a {TARIFA_RAPIDA.tramosApartamentos.map((t) => `$${t.precio}`).join(" / ")} IVA inc.
          según tamaño del edificio. Genera las líneas de abajo (podés ajustarlas a mano después).
        </p>
        <div style={S.quickGrid}>
          <Text label="Apartamentos" type="number" value={quick.apartamentos} onChange={setQuickField("apartamentos")} />
          <Text label="Locales" type="number" value={quick.locales} onChange={setQuickField("locales")} />
          <Text label="Portero" type="number" value={quick.porteros} onChange={setQuickField("porteros")} />
          <Text label="Limpieza" type="number" value={quick.limpiadoras} onChange={setQuickField("limpiadoras")} />
          <Text label="Amenities" type="number" value={quick.amenities} onChange={setQuickField("amenities")} />
          <Text label="Ascensores" type="number" value={quick.ascensores} onChange={setQuickField("ascensores")} />
          <YesNoField label="Garage" value={quick.garages} onChange={setQuickField("garages")} />
          <Text label="Antigüedad (años)" type="number" value={quick.antiguedadAnios} onChange={setQuickField("antiguedadAnios")} />
        </div>
        <button
          type="button"
          onClick={aplicarTarifa}
          disabled={quickQ.subtotal === 0}
          style={{ ...S.quickApply, ...(quickQ.subtotal === 0 ? S.quickApplyDisabled : {}) }}
        >
          {quickQ.subtotal === 0
            ? "Aplicar tarifa"
            : `Aplicar tarifa · ${fmt(quickQ.totalConIva)}/mes IVA inc.`}
        </button>
      </Section>

      {/* ---------- UNIDADES ---------- */}
      <Section title={`Unidades · ${totals.totalUnidades} en total`}>
        <ListEditor
          items={c.unidades}
          fields={[
            { key: "tipo", type: "text", placeholder: "Tipo (ej: Apartamentos)" },
            { key: "cantidad", type: "num", placeholder: "#" },
            { key: "precio", type: "num", placeholder: "$/mes" },
          ]}
          onChange={setArray("unidades")}
        />
      </Section>

      {/* ---------- AMENITIES ---------- */}
      <Section title={`Amenities · ${c.amenities.length} en total`}>
        <ListEditor
          items={c.amenities}
          fields={[
            { key: "nombre", type: "text", placeholder: "Nombre (ej: Piscina)" },
            { key: "precio", type: "num", placeholder: "$/mes" },
          ]}
          onChange={setArray("amenities")}
        />
      </Section>

      {/* ---------- EMPLEADOS ---------- */}
      <Section title={`Empleados · ${totals.totalEmpleados} en total`}>
        <ListEditor
          items={c.empleados}
          fields={[
            { key: "cargo", type: "text", placeholder: "Cargo (ej: Portero)" },
            { key: "cantidad", type: "num", placeholder: "#" },
            { key: "precio", type: "num", placeholder: "$/mes" },
          ]}
          onChange={setArray("empleados")}
        />
      </Section>

      {/* ---------- EXTRAS ---------- */}
      <Section title="Extras · suman al total, sin categoría en el PDF">
        <p style={S.quickHint}>
          Para conceptos como ascensores: se cobran dentro del honorario único pero
          no aparecen como rubro (amenity/personal) en el documento al cliente.
        </p>
        <ListEditor
          items={c.extras || []}
          fields={[
            { key: "concepto", type: "text", placeholder: "Concepto (ej: Ascensor)" },
            { key: "cantidad", type: "num", placeholder: "#" },
            { key: "precio", type: "num", placeholder: "$/mes" },
          ]}
          onChange={setArray("extras")}
        />
      </Section>

      {/* ---------- ESTILO ---------- */}
      <Section title="Estilo de la propuesta económica">
        <RadioGroup
          name="estiloPrecio"
          value={c.estiloPrecio}
          onChange={(v) => setField("estiloPrecio", v)}
          options={[
            { value: "ejecutivo", label: "Ejecutivo", desc: "Numerado, formal" },
            { value: "sello", label: "Sello", desc: "Hero azul, total grande" },
            { value: "banca", label: "Banca", desc: "Tabla limpia, total destacado" },
          ]}
        />
      </Section>

      {/* ---------- PROMO ---------- */}
      <Section title="Promo de bienvenida">
        <Toggle
          label="Aplicar promo"
          value={c.aplicaPromo}
          onChange={(v) => setField("aplicaPromo", v)}
        />
        {c.aplicaPromo && (
          <>
            <Slider
              label="Porcentaje de descuento"
              value={c.promoPorcentaje}
              min={5}
              max={80}
              step={5}
              unit="%"
              onChange={(v) => setField("promoPorcentaje", v)}
            />
            <Slider
              label="Cantidad de meses"
              value={c.promoMeses}
              min={1}
              max={12}
              step={1}
              unit={c.promoMeses === 1 ? "mes" : "meses"}
              onChange={(v) => setField("promoMeses", v)}
            />
          </>
        )}
      </Section>

      {/* ---------- TOTAL CARD ---------- */}
      <div style={S.totalCard}>
        <div style={S.totalLabel}>Total mensual</div>
        <div style={S.totalAmount}>{fmt(totals.total)}</div>
        {c.aplicaPromo && (
          <div style={S.totalPromo}>
            Con promo: <b style={{ color: "#A8D6FF" }}>{fmt(totals.promoTotal)}/mes</b> los primeros {c.promoMeses}{" "}
            {c.promoMeses === 1 ? "mes" : "meses"}
          </div>
        )}
        <div style={S.totalNote}>
          Calculado internamente — no se muestran montos por ítem en el presupuesto al cliente.
        </div>
      </div>

      {/* ---------- ACCION: GENERAR PDF ---------- */}
      {onGenerarPdf && (
        <button
          type="button"
          onClick={onGenerarPdf}
          disabled={generating}
          style={{ ...S.pdfButton, ...(generating ? S.pdfButtonDisabled : {}) }}
        >
          {generating ? "Generando PDF…" : "Descargar PDF"}
        </button>
      )}
    </aside>
  );
}

// ─── Subcomponentes ─────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <section style={S.section}>
      <h3 style={S.sectionTitle}>{title}</h3>
      <div style={S.sectionBody}>{children}</div>
    </section>
  );
}

function Text({ label, value, onChange, type = "text" }) {
  const id = `f-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <input
        id={id}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={S.input}
      />
    </div>
  );
}

/** Campo Sí/No (mismo layout que Text) — value 0 = No, >0 = Sí. */
function YesNoField({ label, value, onChange }) {
  const id = `f-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div style={S.field}>
      <label htmlFor={id} style={S.label}>{label}</label>
      <select
        id={id}
        value={Number(value) > 0 ? "1" : "0"}
        onChange={(e) => onChange(e.target.value === "1" ? 1 : 0)}
        style={S.input}
      >
        <option value="0">No</option>
        <option value="1">Sí</option>
      </select>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <label style={S.toggleRow}>
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: colors.primary, cursor: "pointer" }}
      />
      <span style={{ fontSize: 13, color: colors.gray900 }}>{label}</span>
    </label>
  );
}

function Slider({ label, value, min, max, step, unit, onChange }) {
  return (
    <div style={S.field}>
      <label style={{ ...S.label, display: "flex", justifyContent: "space-between" }}>
        <span>{label}</span>
        <span style={{ color: colors.primary }}>
          {value} {unit}
        </span>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: colors.primary }}
      />
    </div>
  );
}

function RadioGroup({ name, value, onChange, options }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <label key={opt.value} style={{ ...S.radioRow, ...(active ? S.radioRowActive : {}) }}>
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={active}
              onChange={() => onChange(opt.value)}
              style={{ accentColor: colors.primary, cursor: "pointer", marginTop: 2 }}
            />
            <div>
              <div style={{ fontSize: 13, color: colors.gray900 }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: colors.gray600 }}>{opt.desc}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

// ─── Estilos ─────────────────────────────────────────────────────────────────

const S = {
  panel: {
    width: 360,
    flexShrink: 0,
    background: colors.white,
    borderLeft: `1px solid ${colors.gray200}`,
    overflowY: "auto",
    padding: "20px 18px 32px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    fontWeight: 400,
    // Sin height:100vh — el chrome admin (HeaderAdmin + main) ya define la altura disponible.
  },
  header: { marginBottom: 18 },
  headerTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 400,
    color: colors.black,
    letterSpacing: "-0.01em",
  },
  headerSub: {
    margin: "4px 0 0",
    fontSize: 12,
    color: colors.gray600,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  section: {
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: `1px solid ${colors.gray100}`,
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: 11,
    fontWeight: 400,
    color: colors.gray600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  sectionBody: { display: "flex", flexDirection: "column", gap: 10 },
  field: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 11,
    color: colors.gray700,
    fontWeight: 400,
  },
  input: {
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    color: colors.black,
    fontFamily: "inherit",
    fontWeight: 400,
    outline: "none",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    padding: "4px 0",
  },
  quickHint: {
    margin: 0,
    fontSize: 11,
    color: colors.gray600,
    lineHeight: 1.5,
  },
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  quickApply: {
    width: "100%",
    background: colors.primary,
    color: colors.white,
    border: 0,
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.15s ease",
  },
  quickApplyDisabled: {
    background: colors.gray400,
    cursor: "not-allowed",
  },
  radioRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "8px 10px",
    borderRadius: 8,
    border: `1px solid ${colors.gray200}`,
    cursor: "pointer",
    background: colors.white,
  },
  radioRowActive: {
    borderColor: colors.primary,
    background: colors.primarySubtle,
  },
  totalCard: {
    padding: "14px 16px",
    background: "#0A2E5C",
    color: "#fff",
    borderRadius: 10,
    margin: "8px 0",
  },
  totalLabel: {
    fontSize: 10,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    opacity: 0.7,
    marginBottom: 4,
  },
  totalAmount: {
    fontSize: 24,
    letterSpacing: "-0.03em",
    fontVariantNumeric: "tabular-nums",
  },
  totalPromo: {
    fontSize: 11,
    opacity: 0.85,
    marginTop: 4,
  },
  totalNote: {
    fontSize: 10,
    opacity: 0.55,
    marginTop: 6,
    lineHeight: 1.4,
  },
  pdfButton: {
    marginTop: 12,
    width: "100%",
    background: colors.primary,
    color: colors.white,
    border: 0,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    letterSpacing: "-0.005em",
    transition: "background 0.15s ease",
  },
  pdfButtonDisabled: {
    background: colors.gray400,
    cursor: "not-allowed",
  },
};
